<div class="search-container">
  <form [formGroup]="searchForm" (ngSubmit)="onSubmit()" class="search-form">
    <!-- Search bar with buttons on same line -->
    <div class="search-input-group">
      <div class="search-textarea-container">
        <textarea
          formControlName="query"
          placeholder="Search for media&#10;Press Shift+Enter to search&#10;Press Ctrl+Enter for fullscreen search"
          class="search-textarea"
          [class.date-active]="isDateControlActive"
          [disabled]="isSearching"
          rows="2"
          wrap="soft"
          (keydown)="onTextareaKeydown($event)"
        ></textarea>
        <button
          type="button"
          class="textarea-clear-button"
          (click)="clearSearchAndFocus()"
          title="Clear search and focus"
          *ngIf="searchForm.get('query')?.value"
        >
          ✕
        </button>
      </div>
      <div class="search-buttons">

        <!-- Fullscreen Button Group -->
        <div class="fullscreen-button-group">
          <button
            type="button"
            class="fullscreen-search-button"
            [disabled]="isSearchDisabled"
            (click)="onFullscreenSearch()"
            title="Search and open in fullscreen mode (Ctrl+Enter)"
          >
            <span *ngIf="!isSearching">▶️ Play</span>
            <span *ngIf="isSearching" class="loading">🔎...</span>
          </button>
          <div class="fullscreen-mode-selector">
            <button
              type="button"
              class="fullscreen-mode-btn windowed-btn"
              (click)="setFullscreenMode('windowed')"
              [class.active]="fullscreenMode === 'windowed'"
              title="Windowed Fullscreen Mode - Fills browser window but allows scrolling"
            >
              🖼️
            </button>
            <button
              type="button"
              class="fullscreen-mode-btn native-btn"
              (click)="setFullscreenMode('native')"
              [class.active]="fullscreenMode === 'native'"              title="Native Fullscreen Mode - True fullscreen using browser API"            >
              ⛶
            </button>
          </div>
        </div>
  <button
          type="submit"
          class="search-button"
          [disabled]="isSearchDisabled"
        >
          <span *ngIf="!isSearching">Search</span>
          <span *ngIf="isSearching" class="loading">Searching...</span>
        </button>
        <button
          type="button"
          class="settings-button"
          (click)="toggleSettings()"
          [class.active]="showSettings"
          title="API Settings"
        >
          ⚙️
        </button>

        <!-- Filter Groups Button -->
        <button
          type="button"
          class="filter-groups-button"
          (click)="toggleFilterGroups()"
          [class.active]="showFilterGroups"
          title="Filter Groups"
        >
          📋
        </button>

        <!-- Favorites Status -->
        <button
          type="button"
          class="favorites-status"
          *ngIf="favoritesEnabled"
          title="Click to filter your favorites"
          (click)="onFavoritesClick()"
        >
          ❤️ {{ favoritesCount }}
        </button>
      </div>
    </div>
  </form>

  <!-- Advanced Date Controls Panel (visible when no manual date: tag in query) -->
  <div [formGroup]="searchOptionsForm" class="advanced-date-panel" *ngIf="!hasManualDateTag">
    <div class="adp-header">
      <span class="adp-title">📅 Date Filter</span>
      <span class="adp-preview" *ngIf="currentDateTag">{{ currentDateTag }}</span>
      <span class="adp-none" *ngIf="!currentDateTag">No date filter</span>
    </div>

    <!-- Mode selector tabs -->
    <div class="adp-mode-tabs">
      <button type="button" class="adp-tab" [class.active]="advancedDateMode==='none'"
        (click)="setDateMode('none')">None</button>
      <button type="button" class="adp-tab" [class.active]="advancedDateMode==='period'"
        (click)="setDateMode('period')">Period</button>
      <button type="button" class="adp-tab" [class.active]="advancedDateMode==='exact'"
        (click)="setDateMode('exact')">Exact Date</button>
      <button type="button" class="adp-tab" [class.active]="advancedDateMode==='range'"
        (click)="setDateMode('range')">Range</button>
    </div>

    <!-- ── Period mode ── -->
    <div *ngIf="advancedDateMode==='period'" class="adp-content">
      <div class="adp-row">
        <span class="adp-label">Window:</span>
        <select formControlName="periodUnit" class="adp-select">
          <option value="today">Today</option>
          <option value="day">Last 24 h</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
          <option value="year">Last 365 days</option>
        </select>
      </div>
      <div class="adp-nav-row">
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('prev')"
          title="Shift earlier  [">◀ Earlier</button>
        <span class="adp-period-label">{{ periodOffsetLabel }}</span>
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('next')"
          [disabled]="searchOptionsForm.get('periodOffset')?.value === 0"
          title="Shift later  ]">Later ▶</button>
      </div>
      <div class="adp-actions">
        <button type="button" class="adp-apply-btn" (click)="applyDateFilter()">Apply &amp; Search</button>
      </div>
      <div class="adp-hint">Keyboard: <kbd>[</kbd> earlier · <kbd>]</kbd> later</div>
    </div>

    <!-- ── Exact date mode ── -->
    <div *ngIf="advancedDateMode==='exact'" class="adp-content">
      <div class="adp-row">
        <span class="adp-label">Precision:</span>
        <select formControlName="exactGranularity" class="adp-select">
          <option value="year">Year</option>
          <option value="month">Month</option>
          <option value="day">Day</option>
        </select>
      </div>
      <div class="adp-row adp-date-inputs">
        <div class="adp-field">
          <span class="adp-label">Year:</span>
          <input type="number" formControlName="exactYear"
            class="adp-input adp-input-year" min="2000" max="2099" />
        </div>
        <div class="adp-field"
          *ngIf="searchOptionsForm.get('exactGranularity')?.value !== 'year'">
          <span class="adp-label">Month:</span>
          <select formControlName="exactMonth" class="adp-select adp-select-sm">
            <option [value]="1">Jan</option><option [value]="2">Feb</option>
            <option [value]="3">Mar</option><option [value]="4">Apr</option>
            <option [value]="5">May</option><option [value]="6">Jun</option>
            <option [value]="7">Jul</option><option [value]="8">Aug</option>
            <option [value]="9">Sep</option><option [value]="10">Oct</option>
            <option [value]="11">Nov</option><option [value]="12">Dec</option>
          </select>
        </div>
        <div class="adp-field"
          *ngIf="searchOptionsForm.get('exactGranularity')?.value === 'day'">
          <span class="adp-label">Day:</span>
          <input type="number" formControlName="exactDay"
            class="adp-input adp-input-day" min="1" max="31" />
        </div>
      </div>
      <div class="adp-nav-row">
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('prev')"
          title="Previous  [">◀ Prev</button>
        <span class="adp-period-label">{{ exactDateLabel }}</span>
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('next')"
          title="Next  ]">Next ▶</button>
      </div>
      <div class="adp-actions">
        <button type="button" class="adp-apply-btn" (click)="applyDateFilter()">Apply &amp; Search</button>
      </div>
      <div class="adp-hint">Keyboard: <kbd>[</kbd> prev · <kbd>]</kbd> next</div>
    </div>

    <!-- ── Range mode ── -->
    <div *ngIf="advancedDateMode==='range'" class="adp-content">
      <div class="adp-row">
        <span class="adp-label">Granularity:</span>
        <select formControlName="rangeGranularity" class="adp-select">
          <option value="year">Year</option>
          <option value="month">Month</option>
          <option value="day">Day</option>
        </select>
      </div>
      <!-- From -->
      <div class="adp-range-group">
        <span class="adp-range-label">From:</span>
        <div class="adp-date-inputs">
          <div class="adp-field">
            <span class="adp-label">Year:</span>
            <input type="number" formControlName="rangeFromYear"
              class="adp-input adp-input-year" min="2000" max="2099" />
          </div>
          <div class="adp-field"
            *ngIf="searchOptionsForm.get('rangeGranularity')?.value !== 'year'">
            <span class="adp-label">Month:</span>
            <select formControlName="rangeFromMonth" class="adp-select adp-select-sm">
              <option [value]="1">Jan</option><option [value]="2">Feb</option>
              <option [value]="3">Mar</option><option [value]="4">Apr</option>
              <option [value]="5">May</option><option [value]="6">Jun</option>
              <option [value]="7">Jul</option><option [value]="8">Aug</option>
              <option [value]="9">Sep</option><option [value]="10">Oct</option>
              <option [value]="11">Nov</option><option [value]="12">Dec</option>
            </select>
          </div>
          <div class="adp-field"
            *ngIf="searchOptionsForm.get('rangeGranularity')?.value === 'day'">
            <span class="adp-label">Day:</span>
            <input type="number" formControlName="rangeFromDay"
              class="adp-input adp-input-day" min="1" max="31" />
          </div>
        </div>
      </div>
      <!-- To -->
      <div class="adp-range-group">
        <span class="adp-range-label">To:</span>
        <div class="adp-date-inputs">
          <div class="adp-field">
            <span class="adp-label">Year:</span>
            <input type="number" formControlName="rangeToYear"
              class="adp-input adp-input-year" min="2000" max="2099" />
          </div>
          <div class="adp-field"
            *ngIf="searchOptionsForm.get('rangeGranularity')?.value !== 'year'">
            <span class="adp-label">Month:</span>
            <select formControlName="rangeToMonth" class="adp-select adp-select-sm">
              <option [value]="1">Jan</option><option [value]="2">Feb</option>
              <option [value]="3">Mar</option><option [value]="4">Apr</option>
              <option [value]="5">May</option><option [value]="6">Jun</option>
              <option [value]="7">Jul</option><option [value]="8">Aug</option>
              <option [value]="9">Sep</option><option [value]="10">Oct</option>
              <option [value]="11">Nov</option><option [value]="12">Dec</option>
            </select>
          </div>
          <div class="adp-field"
            *ngIf="searchOptionsForm.get('rangeGranularity')?.value === 'day'">
            <span class="adp-label">Day:</span>
            <input type="number" formControlName="rangeToDay"
              class="adp-input adp-input-day" min="1" max="31" />
          </div>
        </div>
      </div>
      <div class="adp-nav-row">
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('prev')"
          title="Shift earlier  [">◀ Earlier</button>
        <span class="adp-period-label">{{ rangeDateLabel }}</span>
        <button type="button" class="adp-nav-btn" (click)="shiftDatePeriod('next')"
          title="Shift later  ]">Later ▶</button>
      </div>
      <div class="adp-actions">
        <button type="button" class="adp-apply-btn" (click)="applyDateFilter()">Apply &amp; Search</button>
      </div>
      <div class="adp-hint">Keyboard: <kbd>[</kbd> earlier · <kbd>]</kbd> later</div>
    </div>
  </div>

  <!-- Nudge when user has a manual date: tag in the search query -->
  <div class="date-tag-nudge" *ngIf="hasManualDateTag">
    <span class="nudge-icon">💡</span>
    <span class="nudge-text">Your search contains a <code>date:</code> tag. Use the
      <strong>Advanced Date Controls</strong> above for easier navigation.</span>
    <button type="button" class="nudge-btn" (click)="removeManualDateTags()">
      Switch to Date Controls
    </button>
  </div>

  <!-- Search Options Panel (Order & Score) -->
  <div *ngIf="shouldShowSearchOptions" class="search-options">
    <div class="search-options-header">
      <h3>Search Options</h3>
      <button
        type="button"
        class="toggle-options-btn"
        (click)="toggleSearchOptions()"
        [class.active]="showSearchOptions"
        title="Advanced Search Options"
      >
        🔧 Options
      </button>
    </div>

    <div *ngIf="showSearchOptions" class="search-options-content">
      <form [formGroup]="searchOptionsForm" class="options-form">

        <!-- Order Section (hidden when user has a manual order: tag) -->
        <div class="option-section" *ngIf="!hasManualOrderTag">
          <h4>Sort Order</h4>
          <div class="order-controls">
            <label for="orderBy">Order by:</label>
            <select id="orderBy" formControlName="orderBy" class="option-select">
              <option value="">Default (ID descending)</option>
              <option value="score">Score (highest first)</option>
              <option value="score_asc">Score (lowest first)</option>
              <option value="favcount">Favorites (most first)</option>
              <option value="random">Random</option>
              <option value="created_at">Upload date (newest first)</option>
              <option value="created_at_asc">Upload date (oldest first)</option>
              <option value="updated_at">Last updated</option>
              <option value="duration">Duration (longest first)</option>
              <option value="duration_asc">Duration (shortest first)</option>
            </select>
          </div>
        </div>
        <!-- Order tag nudge -->
        <div class="option-nudge" *ngIf="hasManualOrderTag">
          <span class="nudge-icon">💡</span>
          <span class="nudge-text">Your search contains an <code>order:</code> tag.
            Use the <strong>Sort Order</strong> control for easier selection.</span>
          <button type="button" class="nudge-btn" (click)="removeManualOrderTags()">
            Switch to Controls
          </button>
        </div>

        <!-- Score Filter Section (hidden when user has a manual score: tag) -->
        <div class="option-section" *ngIf="!hasManualScoreTag">
          <h4>Score Filter</h4>
          <div class="score-controls">
            <div class="score-range">
              <div class="score-input">
                <label for="minScore">Min Score:</label>
                <input
                  type="number"
                  id="minScore"
                  formControlName="minScore"
                  class="option-input"
                  placeholder="e.g. 10"
                  min="-999"
                  max="9999"
                />
              </div>
              <div class="score-input">
                <label for="maxScore">Max Score:</label>
                <input
                  type="number"
                  id="maxScore"
                  formControlName="maxScore"
                  class="option-input"
                  placeholder="e.g. 100"
                  min="-999"
                  max="9999"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- Score tag nudge -->
        <div class="option-nudge" *ngIf="hasManualScoreTag">
          <span class="nudge-icon">💡</span>
          <span class="nudge-text">Your search contains a <code>score:</code> tag.
            Use the <strong>Score Filter</strong> controls for easier management.</span>
          <button type="button" class="nudge-btn" (click)="removeManualScoreTags()">
            Switch to Controls
          </button>
        </div>

        <!-- Action Buttons -->
        <div class="options-actions">
          <button
            type="button"
            class="apply-options-btn"
            (click)="applySearchOptions()"
          >
            Apply & Search
          </button>
          <button
            type="button"
            class="reset-options-btn"
            (click)="resetSearchOptions()"
          >
            Reset
          </button>
        </div>

      </form>
    </div>
  </div>

  <!-- Filter Groups Panel -->
  <div class="filter-groups-panel" *ngIf="showFilterGroups">
    <div class="fg-header">
      <h3>📋 Filter Groups</h3>
      <div class="fg-search-container">
        <input
          type="text"
          class="fg-search-input"
          placeholder="Search filter groups..."
          [(ngModel)]="filterGroupSearch"
        />
        <button
          type="button"
          class="fg-search-clear"
          *ngIf="filterGroupSearch"
          (click)="filterGroupSearch = ''"
        >✕</button>
      </div>
    </div>

    <div class="fg-list">
      <!-- Custom groups section -->
      <div *ngIf="filteredCustomGroups.length > 0" class="fg-section">
        <div class="fg-section-label">My Groups</div>
        <div
          *ngFor="let group of filteredCustomGroups"
          class="fg-item fg-item-custom"
        >
          <div class="fg-item-content" (click)="renamingGroupId !== group.id && applyFilterGroup(group)">
            <span class="fg-item-name" *ngIf="renamingGroupId !== group.id">{{ group.name }}</span>
            <input
              *ngIf="renamingGroupId === group.id"
              type="text"
              class="fg-rename-input"
              [(ngModel)]="renamingGroupName"
              (keydown.enter)="confirmRenameGroup()"
              (keydown.escape)="cancelRenameGroup()"
              (blur)="onRenameBlur()"
              (click)="$event.stopPropagation()"
            />
          </div>
          <div class="fg-item-actions" *ngIf="renamingGroupId !== group.id">
            <button type="button" class="fg-action-btn" (click)="startRenameGroup(group)" title="Rename">✏️</button>
            <button type="button" class="fg-action-btn fg-delete-btn" (click)="deleteFilterGroup(group)" title="Delete">🗑️</button>
          </div>
        </div>
      </div>

      <!-- Default groups section -->
      <div *ngIf="filteredDefaultGroups.length > 0" class="fg-section">
        <div class="fg-section-label">Suggested</div>
        <div
          *ngFor="let group of filteredDefaultGroups"
          class="fg-item fg-item-default"
          [class.fg-item-featured]="group.isFeatured"
          (click)="applyFilterGroup(group)"
        >
          <span class="fg-item-name">{{ group.name }}</span>
        </div>
      </div>

      <!-- No results -->
      <div *ngIf="filteredCustomGroups.length === 0 && filteredDefaultGroups.length === 0" class="fg-empty">
        No filter groups match "{{ filterGroupSearch }}"
      </div>
    </div>

    <div class="fg-footer">
      <div *ngIf="!isAddingFilterGroup" class="fg-add-row">
        <button type="button" class="fg-add-btn" (click)="startAddFilterGroup()">
          ➕ Save Current as Group
        </button>
      </div>
      <div *ngIf="isAddingFilterGroup" class="fg-add-row">
        <input
          type="text"
          class="fg-new-name-input"
          placeholder="Group name..."
          [(ngModel)]="newFilterGroupName"
          (keydown.enter)="confirmAddFilterGroup()"
          (keydown.escape)="cancelAddFilterGroup()"
        />
        <button type="button" class="fg-confirm-btn" (click)="confirmAddFilterGroup()">✓</button>
        <button type="button" class="fg-cancel-btn" (click)="cancelAddFilterGroup()">✕</button>
      </div>
    </div>
  </div>

  <!-- Authentication warning -->
  <div class="auth-warning" *ngIf="authWarning">
    <div class="warning-content">
      <span class="warning-icon">⚠️</span>
      <span class="warning-text">{{ authWarning }}</span>
      <button
        type="button"
        class="warning-close"
        (click)="authWarning = null"
        title="Dismiss warning"
      >
        ✕
      </button>
    </div>
  </div>

  <div class="settings-panel" *ngIf="showSettings">
    <div class="settings-tabs">
      <button type="button" class="settings-tab" [class.active]="activeSettingsTab === 'api'" (click)="setSettingsTab('api')">API</button>
      <button type="button" class="settings-tab" [class.active]="activeSettingsTab === 'filter'" (click)="setSettingsTab('filter')">Filtering</button>
      <button type="button" class="settings-tab" [class.active]="activeSettingsTab === 'theme'" (click)="setSettingsTab('theme')">Theme</button>
      <button type="button" class="settings-tab" [class.active]="activeSettingsTab === 'persistence'" (click)="setSettingsTab('persistence')">Persistence</button>
      <button type="button" class="settings-tab" [class.active]="activeSettingsTab === 'video'" (click)="setSettingsTab('video')">Video</button>
    </div>
    <form [formGroup]="settingsForm" class="settings-form">
      <!-- API Tab -->
      <div *ngIf="activeSettingsTab === 'api'">
        <h3>API Authentication</h3>
        <p class="settings-description">
          Optional: Enter your e621 username and API key for authenticated requests.<br>
          This allows higher rate limits and access to additional content.
        </p>
        <div class="form-group">
          <label for="username">Username:</label>
          <input id="username" type="text" formControlName="username" placeholder="Your e621 username" class="settings-input" />
        </div>
        <div class="form-group">
          <label for="apiKey">API Key:</label>
          <input id="apiKey" type="password" formControlName="apiKey" placeholder="Your e621 API key" class="settings-input" />
        </div>
        <div class="form-group api-test-group">
          <div class="api-test-header">
            <span class="api-test-label">Test Account</span>
            <button type="button" class="api-test-btn" (click)="onTestApi()">
              <span *ngIf="apiTestStatus !== 'pending'">Test Account</span>
              <span *ngIf="apiTestStatus === 'pending'" class="api-test-spinner"></span>
            </button>
          </div>
          <div *ngIf="apiTestStatus" class="api-test-status-message">
            <span [ngClass]="{'success': apiTestStatus === 'success', 'error': apiTestStatus === 'error'}">
              {{ apiTestMessage }}
            </span>
          </div>
        </div>
      </div>
      <!-- Filtering Tab -->
      <div *ngIf="activeSettingsTab === 'filter'">
        <h3>Content Filtering</h3>
        <p class="settings-description">
          Enter tags to blacklist (one per line or space-separated). Posts containing any of these tags will be hidden from search results.
        </p>
        <div class="form-group">
          <label for="blacklistText">Blacklisted Tags:</label>
          <textarea id="blacklistText" formControlName="blacklistText" placeholder="young&#10;gore&#10;scat&#10;watersports" class="settings-textarea" rows="4"></textarea>
          <small class="form-hint">
            Note: Updating the blacklist will clear your search history to prevent index mismatches.
          </small>
        </div>
        <div class="form-group checkbox-group">
          <label for="enableSeenBlacklist" class="checkbox-label">
            <input id="enableSeenBlacklist" type="checkbox" formControlName="enableSeenBlacklist" class="persistence-checkbox" (change)="onPersistenceChange('seenBlacklist', $event)" />
            <span class="checkbox-text">Hide seen media</span>
          </label>
          <small class="form-hint">
            When enabled, media you have already seen will be filtered out of search results.
          </small>
        </div>
      </div>
      <!-- Theme Tab -->
      <div *ngIf="activeSettingsTab === 'theme'">
        <h3>Theme Colors</h3>
        <p class="settings-description">
          Choose a predefined theme or customize individual colors.
        </p>
        <div class="color-settings">
          <div class="form-group">
            <label for="selectedTheme">Preset Themes:</label>
            <select id="selectedTheme" formControlName="selectedTheme" class="theme-selector" (change)="onThemeChange()">
              <option value="" disabled></option>
              <option *ngFor="let theme of colorThemes" [value]="theme.name">{{ theme.name }}</option>
            </select>
          </div>
          <div class="individual-colors">
            <div class="form-group">
              <label for="primaryColor">Primary Color:</label>
              <div class="color-input-group">
                <input id="primaryColor" type="color" formControlName="primaryColor" class="color-picker" (input)="onColorChange()" />
                <input type="text" formControlName="primaryColor" class="color-text-input" placeholder="#e8c446" pattern="^#[0-9A-Fa-f]{6}$" (input)="onColorChange()" />
              </div>
            </div>
            <div class="form-group">
              <label for="secondaryColor">Secondary Color:</label>
              <div class="color-input-group">
                <input id="secondaryColor" type="color" formControlName="secondaryColor" class="color-picker" (input)="onColorChange()" />
                <input type="text" formControlName="secondaryColor" class="color-text-input" placeholder="#25477b" pattern="^#[0-9A-Fa-f]{6}$" (input)="onColorChange()" />
              </div>
            </div>
            <div class="form-group">
              <label for="tertiaryColor">Link/Accent Color:</label>
              <div class="color-input-group">
                <input id="tertiaryColor" type="color" formControlName="tertiaryColor" class="color-picker" (input)="onColorChange()" />
                <input type="text" formControlName="tertiaryColor" class="color-text-input" placeholder="#c8a946" pattern="^#[0-9A-Fa-f]{6}$" (input)="onColorChange()" />
              </div>
            </div>
            <div class="form-group">
              <label for="backgroundColor">Background Color:</label>
              <div class="color-input-group">
                <input id="backgroundColor" type="color" formControlName="backgroundColor" class="color-picker" (input)="onColorChange()" />
                <input type="text" formControlName="backgroundColor" class="color-text-input" placeholder="#000000" pattern="^#[0-9A-Fa-f]{6}$" (input)="onColorChange()" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Persistence Tab -->
      <div *ngIf="activeSettingsTab === 'persistence'">
        <h3>Persistence</h3>
        <p class="settings-description">
          Control which settings and states are remembered between sessions.<br>
          This also allows for tab instances to be independent of each other.
        </p>
        <div class="persistence-settings">
          <div class="form-group checkbox-group">
            <label for="persistVolume" class="checkbox-label">
              <input id="persistVolume" type="checkbox" formControlName="persistVolume" class="persistence-checkbox" (change)="onPersistenceChange('volume', $event)" />
              <span class="checkbox-text">Remember volume level</span>
            </label>
            <small class="form-hint">
              When enabled, your volume and mute settings will be saved and restored between sessions.
            </small>
          </div>
          <div class="form-group checkbox-group">
            <label for="enableSeenTracking" class="checkbox-label">
              <input id="enableSeenTracking" type="checkbox" formControlName="enableSeenTracking" class="persistence-checkbox" (change)="onPersistenceChange('seenTracking', $event)" />
              <span class="checkbox-text">Track seen media</span>
            </label>
            <small class="form-hint">
              When enabled, media you view will be marked as seen. <span class="seen-count-info">Currently tracking {{ seenVideosCount }} seen items.
                <button type="button" class="clear-seen-button" (click)="clearSeenVideos()" [disabled]="seenVideosCount === 0">Clear history</button>
              </span>
            </small>
          </div>
          <div class="form-group checkbox-group">
            <label for="persistQueue" class="checkbox-label">
              <input id="persistQueue" type="checkbox" formControlName="persistQueue" class="persistence-checkbox" (change)="onPersistenceChange('queue', $event)" />
              <span class="checkbox-text">Track queue status</span>
            </label>
            <small class="form-hint">
              When enabled, your current queue (playlist/order) will be saved and restored between sessions.
            </small>
          </div>
        </div>
      </div>
      <!-- Video Tab -->
      <div *ngIf="activeSettingsTab === 'video'">
        <h3>Video Settings</h3>
        <p class="settings-description">
          Configure video playback behavior during slideshow sessions.
        </p>
        <div class="video-settings">
          <div class="form-group checkbox-group">
            <label for="autoLoopEnabled" class="checkbox-label">
              <input id="autoLoopEnabled" type="checkbox" formControlName="autoLoopEnabled" class="persistence-checkbox" />
              <span class="checkbox-text">Auto-loop short videos</span>
            </label>
            <small class="form-hint">
              When enabled, videos will automatically loop until they reach the specified total playback time.
            </small>
          </div>
          <div class="form-group" *ngIf="settingsForm.get('autoLoopEnabled')?.value">
            <label for="autoLoopDuration">Loop current video until total playback time (sec):</label>
            <input id="autoLoopDuration" type="number" formControlName="autoLoopDuration" class="settings-input" min="5" max="300" step="1" placeholder="20" />
            <small class="form-hint">
              Minimum 5 seconds. Videos will loop until they have played for this total duration.
            </small>
          </div>
          
          <!-- Image Settings Section -->
          <div class="settings-section">
            <h4>📸 Image Settings</h4>
            <div class="form-group">
              <label for="imageDisplayDuration">Image display duration (seconds):</label>
              <input id="imageDisplayDuration" type="number" formControlName="imageDisplayDuration" class="settings-input" min="1" max="60" step="1" placeholder="5" />
              <small class="form-hint">
                How long to display static images before auto-advancing. Range: 1-60 seconds.
              </small>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" formControlName="imageAutoAdvance" />
                Auto-advance for images
              </label>
              <small class="form-hint">
                When enabled, images will automatically advance after the display duration.
              </small>
            </div>
            <div class="form-group">
              <label for="animatedGifBehavior">Animated GIF behavior:</label>
              <select id="animatedGifBehavior" formControlName="animatedGifBehavior" class="position-selector">
                <option value="loop">Loop indefinitely (never auto-advance)</option>
                <option value="once">Play once then advance</option>
                <option value="timed">Use display duration</option>
              </select>
              <small class="form-hint">
                Choose how animated GIFs should behave in the slideshow.
              </small>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" formControlName="pinchZoomEnabled" />
                Enable pinch-to-zoom for images (experimental)
              </label>
              <small class="form-hint">
                When enabled, use two fingers to pinch and zoom images on touch devices. Drag to pan while zoomed in. Navigation swipes are disabled while zoomed.
              </small>
            </div>
          </div>
          
          <div class="form-group">
            <label for="fullscreenControlsPosition">Fullscreen controls position:</label>
            <select id="fullscreenControlsPosition" formControlName="fullscreenControlsPosition" class="position-selector">
              <option value="top-left">Top Left</option>
              <option value="top-right">Top Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-right">Bottom Right</option>
            </select>
            <small class="form-hint">
              Choose where the fullscreen controls appear when in fullscreen mode.
            </small>
          </div>
        </div>
      </div>
      <div class="settings-actions">
        <button type="button" (click)="onNukeButtonClick()" class="nuke-settings-button">
          {{ isConfirmingNuke ? 'Confirm?' : 'Nuke Settings' }}
        </button>
        <button type="button" (click)="saveSettings()" class="save-button">
          Save Settings
        </button>
      </div>
    </form>
  </div>
</div>
